import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams, useSearchParams, useLocation } from 'react-router-dom';
import useSWR from 'swr';
import DataTable, { Column } from '../../../components/DataTable/DataTable';
import Badge from '../../../components/ui/Badge';
import {
  Button,
  Card,
  ChevronLeftIcon,
  SearchIcon,
  Select,
  Switch,
} from '../../../components/ui';
import { formatDate as formatDateGlobal } from '../../../config/appConfig';
import { useAccPeriods } from '../periods/useData';
import { fetcher } from '../../../services/fetcher';
import { useNavigateBack, getLocationReturnPath } from '../../../hooks/useNavigateBack';
import { useTheme } from '../../../contexts/ThemeContext';
import { cn } from '../../../lib/utils';
import { ACCOUNTING_TABLE_DEFAULTS } from '../shared/typography';
import { PeriodDateRangeToolbar, ToolbarField } from './shared/reportFields';
import { pickDefaultAccountingPeriod } from './shared/reportUtils';
import { accountingReturnState } from '../shared/navigation';

type LedgerKartelaLine = {
  id: number | null;
  documentId: number | null;
  date: string | null;
  docCode: string | null;
  narrative: string;
  dueDate: string | null;
  companyCode: string;
  linkDisplay: string | null;
  currencyCode: string;
  debitLocal: number;
  creditLocal: number;
  localBalance: number;
  debitUSD: number;
  creditUSD: number;
  usdBalance: number;
  notes: string | null;
  dn: string | null;
  isBroughtForward: boolean;
};

type LedgerKartelaResponse = {
  accountId: number;
  code: string;
  combLevel: string;
  name: string;
  allowOpenItems: boolean;
  dateFrom: string;
  dateTo: string;
  rows: LedgerKartelaLine[];
  totals: {
    recordCount: number;
    debitLocal: number;
    creditLocal: number;
    debitUSD: number;
    creditUSD: number;
    localBalance: number;
    usdBalance: number;
  };
};

type CurrencyOption = { id: number; code: string; name: string };

const formatAmount = (value: number) =>
  value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const formatKartelaAmount = (value: number) => {
  if (value === 0) return '';
  return formatAmount(value);
};

const emptyFooterColumn = {
  footerType: 'custom' as const,
  footerRender: () => null,
};


export const LedgerKartela: React.FC = () => {
  const { colors, theme } = useTheme();
  const isDark = theme === 'dark';
  const location = useLocation();
  const { accountId: accountIdParam } = useParams<{ accountId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();

  const accountId = Number(accountIdParam);
  const appliedDateFrom = searchParams.get('dateFrom') ?? '';
  const appliedDateTo = searchParams.get('dateTo') ?? '';
  const companyIdsParam = searchParams.get('companyIds') ?? '';

  const { data: periodsRaw } = useAccPeriods('', 1, 100);
  const periods = periodsRaw ?? [];

  const [periodId, setPeriodId] = useState('');
  const [draftDateFrom, setDraftDateFrom] = useState(appliedDateFrom);
  const [draftDateTo, setDraftDateTo] = useState(appliedDateTo);
  const [currencyId, setCurrencyId] = useState('');
  const [onlyOpenItems, setOnlyOpenItems] = useState(false);
  const defaultDraftPeriodAppliedRef = useRef(false);

  useEffect(() => {
    defaultDraftPeriodAppliedRef.current = false;
  }, [accountId]);

  useEffect(() => {
    setDraftDateFrom(appliedDateFrom);
    setDraftDateTo(appliedDateTo);
    if (!appliedDateFrom || !appliedDateTo) {
      setPeriodId('');
      return;
    }
    defaultDraftPeriodAppliedRef.current = true;
    const match = periods.find(
      (p) => p.startDate.slice(0, 10) === appliedDateFrom && p.endDate.slice(0, 10) === appliedDateTo,
    );
    setPeriodId(match ? String(match.id) : '');
  }, [appliedDateFrom, appliedDateTo, periods]);

  useEffect(() => {
    if (appliedDateFrom || !periods.length || defaultDraftPeriodAppliedRef.current) return;
    const match = pickDefaultAccountingPeriod(periods);
    if (match) {
      setPeriodId(String(match.id));
      setDraftDateFrom(match.startDate.slice(0, 10));
      setDraftDateTo(match.endDate.slice(0, 10));
      defaultDraftPeriodAppliedRef.current = true;
    }
  }, [appliedDateFrom, periods]);

  useEffect(() => {
    if (!periodId) return;
    const period = periods.find((p) => String(p.id) === periodId);
    if (!period) return;
    setDraftDateFrom(period.startDate.slice(0, 10));
    setDraftDateTo(period.endDate.slice(0, 10));
  }, [periodId, periods]);

  const { data: currenciesRaw } = useSWR<CurrencyOption[] | { data: CurrencyOption[] }>(
    '/currencies?page=1&limit=500',
    fetcher,
  );
  const currencies = Array.isArray(currenciesRaw) ? currenciesRaw : currenciesRaw?.data ?? [];

  const kartelaUrl =
    accountId && appliedDateFrom && appliedDateTo
      ? `/accounting/reports/ledger-kartela?accountId=${accountId}&dateFrom=${appliedDateFrom}&dateTo=${appliedDateTo}${companyIdsParam ? `&companyIds=${companyIdsParam}` : ''}${currencyId ? `&currencyId=${currencyId}` : ''}${onlyOpenItems ? '&onlyOpenItems=1' : ''}`
      : null;

  const { data: kartela, isLoading } = useSWR<LedgerKartelaResponse>(kartelaUrl, fetcher);

  const periodOptions = useMemo(
    () => [
      { value: '', label: 'Custom period' },
      ...periods.map((p) => ({
        value: String(p.id),
        label: `${p.year} (${formatDateGlobal(p.startDate)} – ${formatDateGlobal(p.endDate)})`,
      })),
    ],
    [periods],
  );

  const currencyOptions = useMemo(
    () => [
      { value: '', label: 'All currencies' },
      ...currencies.map((c) => ({ value: String(c.id), label: c.name || c.code })),
    ],
    [currencies],
  );

  const companyScopeLabel = companyIdsParam
    ? `${companyIdsParam.split(',').filter(Boolean).length} companies`
    : 'All companies';

  const applyFilters = useCallback(() => {
    if (!draftDateFrom || !draftDateTo) return;
    const next = new URLSearchParams();
    next.set('dateFrom', draftDateFrom);
    next.set('dateTo', draftDateTo);
    if (companyIdsParam) next.set('companyIds', companyIdsParam);
    setSearchParams(next, { replace: true });
  }, [companyIdsParam, draftDateFrom, draftDateTo, setSearchParams]);

  const backUrl = useMemo(() => {
    const params = new URLSearchParams();
    if (appliedDateFrom) params.set('dateFrom', appliedDateFrom);
    if (appliedDateTo) params.set('dateTo', appliedDateTo);
    if (companyIdsParam) params.set('companyIds', companyIdsParam);
    if (accountId) params.set('selectedAccountId', String(accountId));
    const qs = params.toString();
    return `/accounting/reports/analytical-ledgers${qs ? `?${qs}` : ''}`;
  }, [accountId, appliedDateFrom, appliedDateTo, companyIdsParam]);

  const goBack = useNavigateBack(backUrl, getLocationReturnPath(location.state));

  const canSearch = Boolean(draftDateFrom && draftDateTo);
  const periodDirty = draftDateFrom !== appliedDateFrom || draftDateTo !== appliedDateTo;

  const columns = useMemo((): Column<LedgerKartelaLine>[] => {
    const totals = kartela?.totals;
    const returnState = accountingReturnState(location.pathname, location.search);
    return [
    {
      key: 'date',
      header: 'Date',
      width: '6.5rem',
      footerType: 'custom',
      footerLabel: 'Records',
      footerRender: () => (totals ? String(totals.recordCount) : null),
      render: (_v, row) => (
        <span className={cn('text-sm tabular-nums', colors.text)}>
          {row.date ? formatDateGlobal(row.date) : '—'}
        </span>
      ),
    },
    {
      key: 'docCode',
      header: 'Doc',
      width: '4.5rem',
      ...emptyFooterColumn,
      render: (_v, row) => (
        row.documentId && row.docCode ? (
          <Link
            to={`/accounting/journals/${row.documentId}`}
            state={returnState}
            className={cn('text-sm font-medium tabular-nums hover:underline', colors.primaryFg)}
            onClick={(e) => e.stopPropagation()}
          >
            {row.docCode}
          </Link>
        ) : (
          <span className={cn('text-sm', colors.textMuted)}>{row.docCode ?? '—'}</span>
        )
      ),
    },
    {
      key: 'narrative',
      header: 'Narrative',
      ...emptyFooterColumn,
      render: (_v, row) => (
        <span
          className={cn(
            'block truncate text-sm',
            row.isBroughtForward
              ? cn('font-semibold uppercase tracking-wide', colors.primaryFg)
              : colors.text,
          )}
          title={row.narrative}
        >
          {row.narrative}
        </span>
      ),
    },
    {
      key: 'dueDate',
      header: 'Due',
      width: '6.5rem',
      ...emptyFooterColumn,
      render: (_v, row) => (
        <span className={cn('text-sm tabular-nums', colors.text)}>
          {row.dueDate ? formatDateGlobal(row.dueDate) : ''}
        </span>
      ),
    },
    {
      key: 'companyCode',
      header: 'Cmp',
      width: '5rem',
      ...emptyFooterColumn,
      render: (_v, row) => (
        <span className={cn('truncate', colors.text)} title={row.companyCode}>
          {row.companyCode}
        </span>
      ),
    },
    {
      key: 'linkDisplay',
      header: 'Link',
      width: '8rem',
      ...emptyFooterColumn,
      render: (_v, row) => (
        <span className={cn('text-sm truncate', colors.text)} title={row.linkDisplay ?? undefined}>
          {row.linkDisplay ?? ''}
        </span>
      ),
    },
    {
      key: 'currencyCode',
      header: 'Cur',
      width: '3.5rem',
      ...emptyFooterColumn,
      render: (_v, row) => (
        <span className={colors.text}>{row.currencyCode}</span>
      ),
    },
    {
      key: 'debitLocal',
      header: 'Debit local',
      align: 'right',
      width: '7.5rem',
      footerType: 'custom',
      footerLabel: 'Debit L. sum',
      footerRender: () => (totals ? formatAmount(totals.debitLocal) : null),
      render: (_v, row) => (
        <span
          className={cn(
            'text-sm tabular-nums',
            row.debitLocal > 0 && cn('font-medium', isDark ? 'text-amber-400' : 'text-amber-700'),
          )}
        >
          {formatKartelaAmount(row.debitLocal)}
        </span>
      ),
    },
    {
      key: 'creditLocal',
      header: 'Credit local',
      align: 'right',
      width: '7.5rem',
      footerType: 'custom',
      footerLabel: 'Credit L. sum',
      footerRender: () => (totals ? formatAmount(totals.creditLocal) : null),
      render: (_v, row) => (
        <span className={cn('text-sm tabular-nums', colors.text)}>
          {formatKartelaAmount(row.creditLocal)}
        </span>
      ),
    },
    {
      key: 'localBalance',
      header: 'Local blnc',
      align: 'right',
      width: '7.5rem',
      footerType: 'custom',
      footerLabel: 'Local balance',
      footerRender: () => (totals ? formatAmount(totals.localBalance) : null),
      render: (_v, row) => (
        <span className={cn('text-sm tabular-nums font-medium', colors.text)}>
          {formatKartelaAmount(row.localBalance)}
        </span>
      ),
    },
    {
      key: 'debitUSD',
      header: 'Debit USD',
      align: 'right',
      width: '7rem',
      footerType: 'custom',
      footerLabel: 'Debit USD sum',
      footerRender: () => (totals ? formatAmount(totals.debitUSD) : null),
      render: (_v, row) => (
        <span className={cn('text-sm tabular-nums', colors.text)}>
          {formatKartelaAmount(row.debitUSD)}
        </span>
      ),
    },
    {
      key: 'creditUSD',
      header: 'Credit USD',
      align: 'right',
      width: '7rem',
      footerType: 'custom',
      footerLabel: 'Credit USD sum',
      footerRender: () => (totals ? formatAmount(totals.creditUSD) : null),
      render: (_v, row) => (
        <span className={cn('text-sm tabular-nums', colors.text)}>
          {formatKartelaAmount(row.creditUSD)}
        </span>
      ),
    },
    {
      key: 'usdBalance',
      header: 'USD blnc',
      align: 'right',
      width: '7rem',
      footerType: 'custom',
      footerLabel: 'USD balance',
      footerRender: () => (totals ? formatAmount(totals.usdBalance) : null),
      render: (_v, row) => (
        <span className={cn('text-sm tabular-nums font-medium', colors.text)}>
          {formatKartelaAmount(row.usdBalance)}
        </span>
      ),
    },
    {
      key: 'notes',
      header: 'Notes',
      width: '6rem',
      ...emptyFooterColumn,
      render: (_v, row) => (
        <span className={cn('text-sm truncate', colors.textMuted)} title={row.notes ?? undefined}>
          {row.notes ?? ''}
        </span>
      ),
    },
    {
      key: 'dn',
      header: 'DN',
      width: '4rem',
      ...emptyFooterColumn,
      render: (_v, row) => (
        <span className={cn('text-sm', colors.textMuted)}>{row.dn ?? ''}</span>
      ),
    },
  ];
  }, [colors.primaryFg, colors.text, colors.textMuted, isDark, kartela?.totals, location.pathname, location.search]);

  if (!accountId) {
    return (
      <div className="p-6 space-y-4">
        <p className={cn('text-sm', colors.textMuted)}>
          Missing ledger. Open a ledger from Analytical ledgers.
        </p>
        <Button variant="outline" onClick={goBack}>
          Back to Analytical ledgers
        </Button>
      </div>
    );
  }

  const accountTitle = kartela ? kartela.name : 'Ledger details';

  return (
    <div className="flex flex-col h-full min-h-0 p-1 gap-1">
      <Card className="p-0 shrink-0 overflow-hidden">
        <div
          className={cn(
            'flex min-h-[3.25rem] items-center gap-3 px-4 py-3',
            colors.primary,
            colors.primaryText,
          )}
        >
          <button
            type="button"
            onClick={goBack}
            className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-white/90 transition-colors hover:bg-white/10"
            title="Back to Analytical ledgers"
            aria-label="Back to Analytical ledgers"
          >
            <ChevronLeftIcon className="h-4 w-4" />
          </button>
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-semibold text-white">{accountTitle}</p>
            {kartela && (
              <p className="mt-0.5 truncate text-xs text-white/75">{kartela.name}</p>
            )}
          </div>
          <Badge variant="secondary" className="shrink-0 border-white/20 bg-white/15 text-white hover:bg-white/15">
            Ledger
          </Badge>
        </div>

        <div className="px-4 py-2.5">
          <div className="flex items-center gap-3 flex-nowrap overflow-x-auto dashboard-scrollbar">
            <PeriodDateRangeToolbar
              periodId={periodId}
              periodOptions={periodOptions}
              onPeriodChange={setPeriodId}
              dateFrom={draftDateFrom}
              dateTo={draftDateTo}
              onDateFromChange={(value) => {
                setPeriodId('');
                setDraftDateFrom(value);
              }}
              onDateToChange={(value) => {
                setPeriodId('');
                setDraftDateTo(value);
              }}
            />

            <ToolbarField label="Ledger">
              <div
                className={cn(
                  'flex h-10 min-w-[9.5rem] items-center rounded-lg border px-3',
                  colors.headerBg,
                  colors.border,
                )}
                title={kartela?.name}
              >
                <span className={cn('font-semibold tabular-nums tracking-tight', colors.text)}>
                  {kartela?.combLevel ?? '—'}
                </span>
              </div>
            </ToolbarField>

            <ToolbarField label="Currency">
              <div className="w-[8rem]">
                <Select value={currencyId} onChange={(e) => setCurrencyId(e.target.value)} options={currencyOptions} />
              </div>
            </ToolbarField>

            {kartela?.allowOpenItems && (
              <Switch
                label="Only open items"
                checked={onlyOpenItems}
                onChange={(e) => setOnlyOpenItems(e.target.checked)}
                size="sm"
              />
            )}

            <div className="flex items-center gap-2 ml-auto shrink-0">
              <Badge variant="secondary" className="h-10 px-3 flex items-center gap-1.5 font-normal whitespace-nowrap">
                <span className={cn('h-1.5 w-1.5 rounded-full shrink-0', companyIdsParam ? 'bg-amber-500' : 'bg-emerald-500')} />
                {companyScopeLabel}
              </Badge>
              {periodDirty && (
                <Badge variant="warning" className="h-10 px-3 flex items-center font-normal whitespace-nowrap">
                  Period changed
                </Badge>
              )}
              <Button
                variant="default"
                onClick={applyFilters}
                disabled={!canSearch}
                className="h-10 px-5 gap-2 shrink-0"
              >
                <SearchIcon className="h-4 w-4" />
                Search
              </Button>
            </div>
          </div>
        </div>
      </Card>

      <div
        className={cn(
          'flex flex-1 min-h-0 flex-col overflow-hidden rounded-xl border shadow-sm',
          colors.surface,
          colors.border,
        )}
      >
        <div className={cn('flex-shrink-0 px-4 py-2 border-b text-sm', colors.border, colors.surfaceHover)}>
          {appliedDateFrom && appliedDateTo ? (
            <span className={colors.textMuted}>
              Showing movements for{' '}
              <span className={cn('font-medium', colors.text)}>
                {formatDateGlobal(appliedDateFrom)} – {formatDateGlobal(appliedDateTo)}
              </span>
              {kartela && (
                <>
                  {' · '}
                  <span className={cn('font-medium tabular-nums', colors.text)}>{kartela.totals.recordCount}</span>
                  {' '}
                  {kartela.totals.recordCount === 1 ? 'record' : 'records'}
                </>
              )}
            </span>
          ) : (
            <span className={colors.textMuted}>Set a period and click Search to load the ledger.</span>
          )}
        </div>

        <div className="flex-1 min-h-0">
          {isLoading && (
            <div className={cn('flex items-center justify-center h-full text-sm', colors.textMuted)}>
              Loading ledger details…
            </div>
          )}
          {!isLoading && !appliedDateFrom && (
            <div className={cn('flex flex-col items-center justify-center h-full px-8 text-center', colors.textMuted)}>
              <SearchIcon className="h-8 w-8 opacity-40 mb-3" />
              <p className="text-sm font-medium">Choose a period</p>
              <p className="text-xs mt-1 max-w-sm">Select dates or an accounting period, then click Search.</p>
            </div>
          )}
          {!isLoading && appliedDateFrom && appliedDateTo && kartela && (
            <DataTable
              {...ACCOUNTING_TABLE_DEFAULTS}
              data={kartela.rows}
              columns={columns}
              keyExtractor={(row) => row.id ?? `bf-${row.date ?? 'na'}-${row.localBalance}`}
              emptyMessage="No journal lines for this ledger in the selected period."
              showFooter
              stickyHeader
              tableWrapperClassName="inline-block min-w-max align-middle"
              containerClassName="h-full min-h-0 border-0 shadow-none rounded-none"
              tableClassName={cn('min-w-[72rem] table-fixed divide-y', colors.border)}
            />
          )}
        </div>
      </div>
    </div>
  );
};
